import React, { useState, useEffect, useRef } from "react";
import { DOG_BREEDS } from "../data/dogBreeds";
import { fetchDogDescription, fetchDogImage, removeBlueBackground, ACTIONS } from "../utils/dogCardGenerator";
import { motion } from "framer-motion";
import emotionStyled from "@emotion/styled";
import html2canvas from "html2canvas";
import { Typography, Box, IconButton, Tooltip } from "@mui/material";
import { styled as muiStyled } from "@mui/system";
import { useAuth } from "../contexts/AuthContext";
import AuthPopup from "./AuthPopup";

// Additional icons for navigation and actions
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import HearingIcon from '@mui/icons-material/Hearing';

const AUDIO_BASE_URL = "https://media.satoshiadvance.com/audio";
const MEDIA_BASE_URL = "https://media.satoshiadvance.com/images";
const PUBLIC_URL = process.env.PUBLIC_URL || "";
const PLACEHOLDER_IMAGE = `${PUBLIC_URL}${PUBLIC_URL.endsWith("/") ? "" : "/"}placeholder.png`;

const SectionTitle = muiStyled(Typography)({
  fontFamily: "'Press Start 2P', cursive",
  color: "#FFFFFF",
  fontSize: "2.4rem",
  marginTop: "40px",
  marginBottom: "20px",
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  "@media (max-width: 600px)": {
    fontSize: "1.2rem", // Match the other section titles (Education & Resources)
  },
});

export const DividerBar = emotionStyled(motion.div)`
  width: 80%;
  height: 5px;
  background-color: #FFFFFF;
  margin: 20px auto 0;
  margin-bottom: 30px;
  transform-origin: center;
  box-shadow: 
    0 0 10px #FFFFFF,
    0 0 20px #FF69B4,
    0 0 30px #FF69B4,
    0 0 40px #FF69B4,
    0 0 70px #FFFFFF,
    0 0 80px #FFFFFF,
    0 0 100px #FF69B4,
    0 0 150px #FF69B4;
  
  @media (max-width: 600px) {
    width: 95%;
  }
`;

export const MotionDivider = () => {
  return (
    <DividerBar
      initial={{ scaleX: 0 }} // Start as a point in the middle
      animate={{
        scaleX: 1, // Grow to full width
        rotateX: 15, // Slight tilt for a 3D effect
      }}
      transition={{
        duration: 2, // Grow over 2 seconds
        ease: "easeInOut", // Smooth animation
      }}
    />
  );
};

const DOG_TUNES = [
  "DogTunes7.mp3",
  "DogTunes5.mp3",
  "DogTunes3.mp3",
  "DogTunes4.mp3",
  "DogTunes2.mp3",
  "DogTunes6.mp3",
  "DogTunes1.mp3",
  "DogTunes8.mp3",
  "DogTunes9.mp3",
  "DogTunes10.mp3",
  "DogTunes11.mp3",
  "DogTunes12.mp3",
  "DogTunes13.mp3",
  "DogTunes14.mp3",
  "DogTunes15.mp3",
];

// Retro-styled tooltip for the four dog action buttons
// (Search / Download / Learn / Command).
const dogButtonTooltipProps = {
  tooltip: {
    sx: {
      fontFamily: "'Press Start 2P', 'Courier New', monospace",
      fontSize: "10px",
      backgroundColor: "#1a237e",
      padding: "8px 12px",
      letterSpacing: "0.5px",
      border: "1px solid #00BFFF",
    },
  },
  arrow: { sx: { color: "#1a237e" } },
};

function DogSprite() {
  const [showLargeImage, setShowLargeImage] = useState(false);
  const audioRef = useRef(new Audio(`${AUDIO_BASE_URL}/dog-barking.wav`));
  const backAudioRef = useRef(new Audio(`${AUDIO_BASE_URL}/dogs-bark-english-sheep.wav`));
  const trickAudioRef = useRef(new Audio(`${AUDIO_BASE_URL}/dogs-bark-golden-retriever.wav`));
  const [currentBreed, setCurrentBreed] = useState("");
  const [currentDescription, setCurrentDescription] = useState("");
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [isSpinning, setIsSpinning] = useState({ download: false, play: false, action: false });
  const [previousBreeds, setPreviousBreeds] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state for next breed button
  const [loadingAction, setLoadingAction] = useState(false); // Loading state for action button
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is the first load
  const downloadAudioRef = useRef(new Audio(`${AUDIO_BASE_URL}/dogs-bark-jack-russell.wav`));
  const [attemptsLeft, setAttemptsLeft] = useState(3); // Number of attempts remaining
  const [result, setResult] = useState(null); // Game result: "Winner!" or "Game Over"
  // When the result overlay appeared. Mobile browsers fire a synthetic click
  // ~300ms after the winning tap; without this guard that ghost click lands on
  // the overlay and instantly dismisses the Winner!/Game Over screen.
  const resultShownAtRef = useRef(0);
  useEffect(() => {
    if (result) resultShownAtRef.current = Date.now();
  }, [result]);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [gameStarted, setGameStarted] = useState(false);
  const [dogTunesPaths] = useState(DOG_TUNES.map((file) => `${AUDIO_BASE_URL}/${file}`));
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState("low");
  const [musicPlayedBeforeSpeech, setMusicPlayedBeforeSpeech] = useState(false);
  // Lazily created so the browser doesn't start downloading a music track
  // on page load — preload "none" defers the fetch until the user hits play.
  const musicAudioRef = useRef(null);
  if (musicAudioRef.current === null) {
    musicAudioRef.current = new Audio();
    musicAudioRef.current.preload = "none";
  }
  // Counts consecutive tracks that failed to load so a broken file gets
  // skipped, but a fully dead playlist doesn't loop forever.
  const failedTracksRef = useRef(0);
  const [transparentSpriteUrl, setTransparentSpriteUrl] = useState(null);
  const [processedSpriteUrl, setProcessedSpriteUrl] = useState(null);
  // Double-click/double-tap on the sprite or description hands THIS exact
  // dog (art + description) to the 3D voxel app on dogs.satoshiadvance.com
  const lastSpriteTapRef = useRef(0);
  const handoffInFlightRef = useRef(false);
  const openIn3DVoxels = async () => {
    if (!currentBreed || !currentImageUrl || handoffInFlightRef.current) return;
    handoffInFlightRef.current = true;
    try {
      const response = await fetch(
        "https://satoshi-homepage-59650ad0a3ce.herokuapp.com/api/dog-handoff",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            breed: currentBreed,
            image: processedSpriteUrl || currentImageUrl,
            description: currentDescription
          })
        }
      );
      const data = await response.json();
      if (data && data.id) {
        window.location.href = `https://dogs.satoshiadvance.com/?handoff=${data.id}`;
        return;
      }
      throw new Error(data?.error || "No handoff id");
    } catch (error) {
      console.error("3D handoff failed:", error);
      handoffInFlightRef.current = false;
    }
  };
  // Manual double-tap detection: not every mobile browser synthesizes
  // dblclick from a double tap
  const handleSpriteTap = () => {
    const now = Date.now();
    if (now - lastSpriteTapRef.current < 350) openIn3DVoxels();
    lastSpriteTapRef.current = now;
  };
  const [miniGameData, setMiniGameData] = useState(null);
  const [wrongGuess, setWrongGuess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 60 second timer
  const [imageLoaded, setImageLoaded] = useState(false); // Track if background image loaded
  const timerIntervalRef = useRef(null);
  const [breedGenerationCount, setBreedGenerationCount] = useState(0); // Track breed generations for unauthenticated users
  const [miniGamePlays, setMiniGamePlays] = useState(0); // Track mini-game plays for unauthenticated users

  // Replace the constant hotspot with a state variable
  const [hotspot, setHotspot] = useState({
    x1: 5925, // Left boundary
    y1: 5050, // Top boundary
    x2: 6550, // Right boundary
    y2: 5550, // Bottom boundary
  });


  const setShowLargeImageWithSprite = async () => {
    // Check if unauthenticated user has already played once
    if (!isAuthenticated && miniGamePlays >= 1) {
      setShowAuthPopup(true);
      return;
    }

    // Increment mini-game play count for unauthenticated users
    if (!isAuthenticated) {
      setMiniGamePlays(prev => prev + 1);
    }

    try {
      // Only process if we have a current breed and image
      if (currentBreed && currentImageUrl) {
        setLoadingMessage("Processing sprite...");
        setImageLoaded(false); // Reset image loaded state

        // Remove blue background from current sprite (reuse processed sprite when available)
        const spriteSource = processedSpriteUrl || currentImageUrl;
        const transparentSprite = processedSpriteUrl
          ? spriteSource
          : await removeBlueBackground(spriteSource);

        if (!processedSpriteUrl && transparentSprite && transparentSprite !== PLACEHOLDER_IMAGE) {
          setProcessedSpriteUrl(transparentSprite);
        }

        setTransparentSpriteUrl(transparentSprite);

        // Position the sprite in the mini-game
        const gameData = positionSpriteInMiniGame(transparentSprite, currentBreed);
        setMiniGameData(gameData);

        // Explicitly log the hotspot for debugging
        const newHotspot = {
          x1: gameData.position.x,
          y1: gameData.position.y,
          x2: gameData.position.x + gameData.position.width,
          y2: gameData.position.y + gameData.position.height
        };

        console.log("Setting hotspot to:", newHotspot);

        // Update the hotspot boundaries with the new position
        setHotspot(newHotspot);

        // Reset game state whenever showing the large image
        setAttemptsLeft(3);
        setResult(null);
        setGameStarted(false);
        setTimeLeft(60); // Reset timer to 60 seconds
        setLoadingMessage("Loading image...");

        // Now show the large image/mini-game
        setShowLargeImage(true);
      } else {
        console.error('Current breed or image not available');
      }
    } catch (error) {
      console.error('Error preparing sprite for mini-game:', error);
      // Fallback to original behavior if processing fails
      setShowLargeImage(true);
    }
  };



  // New state for auth popup
  const [showAuthPopup, setShowAuthPopup] = useState(false);

  // Use the auth context
  const { user, cardGenerationInfo, recordCardGeneration, markFreeGenerationUsed, isAuthenticated, saveCard } = useAuth();



  const cardRef = useRef(null);





  // Helper function to preload images for smoother display
  const preloadImage = (url) =>
    new Promise((resolve) => {
      const img = new Image();
      const safeUrl = url || PLACEHOLDER_IMAGE;

      img.onload = () => resolve();
      img.onerror = () => {
        console.warn(`Failed to preload image at ${safeUrl}, falling back to placeholder sprite.`);
        if (safeUrl !== PLACEHOLDER_IMAGE) {
          img.src = PLACEHOLDER_IMAGE;
        } else {
          resolve();
        }
      };

      img.src = safeUrl;
    });

  // Modified showNextBreed to generate on-demand with loading indicator
  const showNextBreed = async () => {
    // Check if unauthenticated user has reached 5 generation limit
    if (!isAuthenticated && breedGenerationCount >= 5) {
      setShowAuthPopup(true);
      return;
    }

    // For authenticated users, check monthly limits
    if (isAuthenticated) {
      if (cardGenerationInfo.remainingGenerations <= 0) {
        alert("You've reached your monthly limit of 25 dog breed cards. Come back next month for more!");
        return;
      }
      const success = await recordCardGeneration(user.email);
      if (!success) {
        return;
      }
    }

    // Increment generation count for unauthenticated users
    if (!isAuthenticated) {
      setBreedGenerationCount(prev => prev + 1);
    }

    proceedWithBreedGeneration();
  };

  // Generate breed on-demand without preloading
  const proceedWithBreedGeneration = async () => {
    setLoading(true);
    setIsInitialLoad(false);
    playBarkSound();

    // Save current breed to history
    if (currentBreed) {
      setPreviousBreeds((prev) => [
        ...prev,
        {
          breed: currentBreed,
          description: currentDescription,
          imageUrl: currentImageUrl,
        },
      ]);
    }

    try {
      // Generate new breed on-demand
      const randomIndex = Math.floor(Math.random() * DOG_BREEDS.length);
      const newBreed = DOG_BREEDS[randomIndex];

      console.log("Generating new breed:", newBreed);

      const [description, imageUrl] = await Promise.all([
        fetchDogDescription(newBreed),
        fetchDogImage(newBreed),
      ]);

      // Preload image for smoother display
      await preloadImage(imageUrl);

      const finalImageUrl = await applySpriteProcessing(imageUrl);

      // Update current breed
      setCurrentBreed(newBreed || "Unknown Breed");
      setCurrentDescription(description || "Unable to load description.");
      setCurrentImageUrl(finalImageUrl || PLACEHOLDER_IMAGE);
      setTransparentSpriteUrl(null);

    } catch (error) {
      console.error("Error generating next breed:", error);
      setCurrentBreed("Error");
      setCurrentDescription("Unable to load description.");
      setCurrentImageUrl(PLACEHOLDER_IMAGE);
      setProcessedSpriteUrl(null);
      setTransparentSpriteUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const navigateBack = () => {
    if (previousBreeds.length > 0) {
      playBackBarkSound();
      const lastBreed = previousBreeds[previousBreeds.length - 1];
      setPreviousBreeds((prev) => prev.slice(0, -1));
      setCurrentBreed(lastBreed.breed);
      setCurrentDescription(lastBreed.description);
      setCurrentImageUrl(lastBreed.imageUrl);
      setProcessedSpriteUrl(
        lastBreed.imageUrl && lastBreed.imageUrl !== PLACEHOLDER_IMAGE
          ? lastBreed.imageUrl
          : null
      );
      setTransparentSpriteUrl(null);
    }
  };

  // Generate random action on-demand with loading indicator (no preloading)
  const generateRandomAction = async () => {
    if (!currentBreed) return;

    // Check if unauthenticated user has reached 5 generation limit
    if (!isAuthenticated && breedGenerationCount >= 5) {
      setShowAuthPopup(true);
      return;
    }

    // Increment generation count for unauthenticated users
    if (!isAuthenticated) {
      setBreedGenerationCount(prev => prev + 1);
    }

    setLoadingAction(true); // Start loading indicator
    playTrickBarkSound();

    try {
      // Always generate fresh action for current breed on-demand
      const randomAction = ACTIONS[Math.floor(Math.random() * ACTIONS.length)];
      console.log(`Generating action "${randomAction}" for breed "${currentBreed}"`);

      const imageUrl = await fetchDogImage(currentBreed, randomAction);

      // Preload image for smoother display
      await preloadImage(imageUrl);

      const finalImageUrl = await applySpriteProcessing(imageUrl);

      setCurrentImageUrl(finalImageUrl || PLACEHOLDER_IMAGE);
      setTransparentSpriteUrl(null);

    } catch (error) {
      console.error("Error generating random action:", error);
      setCurrentImageUrl(PLACEHOLDER_IMAGE);
      setProcessedSpriteUrl(null);
      setTransparentSpriteUrl(null);
    } finally {
      setLoadingAction(false); // Stop loading indicator
    }
  };



  const applySpriteProcessing = async (imageUrl) => {
    if (!imageUrl || imageUrl === PLACEHOLDER_IMAGE) {
      setProcessedSpriteUrl(null);
      return PLACEHOLDER_IMAGE;
    }

    try {
      const cleanedSprite = await removeBlueBackground(imageUrl);

      if (cleanedSprite && cleanedSprite !== PLACEHOLDER_IMAGE) {
        setProcessedSpriteUrl(cleanedSprite);
        return cleanedSprite;
      }

      setProcessedSpriteUrl(null);
      return imageUrl;
    } catch (processingError) {
      console.error('Error processing sprite image:', processingError);
      setProcessedSpriteUrl(null);
      return imageUrl;
    }
  };

  const positionSpriteInMiniGame = (spriteUrl, currentBreed) => {
    // Create an object to return the sprite's position and the updated mini-game data
    const gameData = {
      spriteUrl: '',
      breed: currentBreed,
      position: {
        x: 0,
        y: 0,
        width: 0,
        height: 0
      }
    };

    // The game-image-container size depends on viewport — must match the
    // dimensions set at the JSX level so the sprite stays inside the image.
    const isMobile = window.innerWidth < 600;
    const containerWidth = isMobile ? 5120 : 10240;
    const containerHeight = isMobile ? 2880 : 5760;
    const spriteWidth = 280;
    const spriteHeight = 280;
    const margin = 250; // keep sprite away from edges so it's not half-clipped

    const minX = margin;
    const maxX = containerWidth - spriteWidth - margin;
    const minY = margin;
    const maxY = containerHeight - spriteHeight - margin;

    gameData.position.x = Math.floor(Math.random() * (maxX - minX)) + minX;
    gameData.position.y = Math.floor(Math.random() * (maxY - minY)) + minY;
    gameData.position.width = spriteWidth;
    gameData.position.height = spriteHeight;

    // Set the sprite URL
    gameData.spriteUrl = spriteUrl;

    return gameData;
  };

  const handleImageClick = (e) => {
    e.stopPropagation();

    if (result) return;

    const rect = e.target.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    console.log(`Click at X: ${clickX}, Y: ${clickY}`);
    console.log(
      `Hotspot: X1=${hotspot.x1}, Y1=${hotspot.y1}, X2=${hotspot.x2}, Y2=${hotspot.y2}`
    );

    if (clickX >= hotspot.x1 && clickX <= hotspot.x2 && clickY >= hotspot.y1 && clickY <= hotspot.y2) {
      console.log("You clicked inside the hotspot!");
      setResult("Winner!");
    } else {
      console.log("You clicked outside the hotspot.");
      setWrongGuess(true); // Set wrong guess state to true

      // Reset wrong guess state after animation finishes
      setTimeout(() => {
        setWrongGuess(false);
      }, 800);

      setAttemptsLeft((prev) => {
        const newAttempts = prev - 1;
        if (newAttempts <= 0) {
          setResult("Game Over");
        }
        return newAttempts;
      });

    }
  };

  const downloadCard = async () => {
    if (cardRef.current) {
      setIsSpinning((prev) => ({ ...prev, download: true }));
      playDownloadBarkSound();

      try {
        const canvas = await html2canvas(cardRef.current, {
          backgroundColor: null,
          useCORS: true,
          allowTaint: true,
          imageTimeout: 0,
          logging: true,
        });

        const dataURL = canvas.toDataURL("image/png");

        // Save to user's account if logged in and saveCard function exists
        if (isAuthenticated && typeof saveCard === 'function') {
          // Also save the isolated transparent sprite (background removed,
          // like the search game) so merch can print just the dog.
          let spriteUrl = processedSpriteUrl || null;
          if (!spriteUrl && currentImageUrl) {
            try {
              spriteUrl = await removeBlueBackground(currentImageUrl);
            } catch (spriteError) {
              spriteUrl = null;
            }
          }
          saveCard(currentBreed, dataURL, spriteUrl).catch(error =>
            console.error("Error saving card to server:", error)
          );
        }

        // Download to user's device (this already worked before)
        const link = document.createElement("a");
        link.href = dataURL;
        link.download = `${currentBreed.replace(/\s+/g, "_")}dogcard.png`;
        link.click();
      } catch (error) {
        console.error("Error generating card:", error);
      } finally {
        setIsSpinning((prev) => ({ ...prev, download: false }));
      }
    }
  };

  const speakDescription = () => {
    if ("speechSynthesis" in window) {
      const synth = window.speechSynthesis;
      if (musicPlayedBeforeSpeech && volumeLevel === "high") {
        setVolumeLevel("low");
        if (musicAudioRef.current) {
          musicAudioRef.current.volume = 0.3;
        }
      }

      setMusicPlayedBeforeSpeech(false);

      const utterance = new SpeechSynthesisUtterance(
        `${currentBreed}: ${currentDescription}`
      );
      utterance.lang = "en-US";
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      setIsSpinning((prev) => ({ ...prev, play: true }));
      setTimeout(() => {
        setIsSpinning((prev) => ({ ...prev, play: false }));
        synth.speak(utterance);
      }, 500);
    } else {
      alert("Speech Synthesis not supported in this browser.");
    }
  };

  // Load a custom card generated from the dashboard (breed, description,
  // image and transparent sprite) so all game features work on it
  const loadCustomCard = (card) => {
    if (!card || !card.breed || !card.imageUrl) return;
    setCurrentBreed(card.breed);
    setCurrentDescription(card.description || "");
    setCurrentImageUrl(card.imageUrl);
    setProcessedSpriteUrl(card.spriteUrl || null);
    setTransparentSpriteUrl(card.spriteUrl || null);
    setIsInitialLoad(false);
    setLoading(false);
  };

  // Live handoff: dashboard dispatches this while the game is mounted
  useEffect(() => {
    const handler = (e) => {
      try {
        sessionStorage.removeItem('customDogCard');
      } catch (err) {}
      loadCustomCard(e.detail);
    };
    window.addEventListener('loadCustomDogCard', handler);
    return () => window.removeEventListener('loadCustomDogCard', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize first breed on load (no preloading)
  useEffect(() => {
    const initializeBreeds = async () => {
      // A card handed off from the dashboard takes priority over a random breed
      try {
        const storedCard = sessionStorage.getItem('customDogCard');
        if (storedCard) {
          sessionStorage.removeItem('customDogCard');
          loadCustomCard(JSON.parse(storedCard));
          return;
        }
      } catch (err) {
        console.error('Error loading custom dog card:', err);
      }

      setLoading(true);

      try {
        const randomIndex = Math.floor(Math.random() * DOG_BREEDS.length);
        const initialBreed = DOG_BREEDS[randomIndex];

        console.log("Initializing first breed:", initialBreed);

        const [description, imageUrl] = await Promise.all([
          fetchDogDescription(initialBreed),
          fetchDogImage(initialBreed),
        ]);

        // Preload image for smoother display
        await preloadImage(imageUrl);

        const finalImageUrl = await applySpriteProcessing(imageUrl);

        setCurrentBreed(initialBreed);
        setCurrentDescription(description);
        setCurrentImageUrl(finalImageUrl || PLACEHOLDER_IMAGE);
        setTransparentSpriteUrl(null);
        setIsInitialLoad(false); // Mark initial load as complete

        // Count initial breed as first generation for unauthenticated users
        if (!isAuthenticated) {
          setBreedGenerationCount(1);
        }

      } catch (error) {
        console.error("Error initializing breeds:", error);
        setCurrentBreed("Error");
        setCurrentDescription("Unable to load description.");
        setCurrentImageUrl(PLACEHOLDER_IMAGE);
        setProcessedSpriteUrl(null);
        setTransparentSpriteUrl(null);
      } finally {
        setLoading(false);
      }
    };

    initializeBreeds();
  }, []);

  useEffect(() => {
    musicAudioRef.current.src = dogTunesPaths[currentTrackIndex];

    if (isMusicPlaying) {
      musicAudioRef.current.play().catch((err) => {
        console.log("Music autoplay error:", err);
      });
    }
  }, [currentTrackIndex, isMusicPlaying, dogTunesPaths]);

  // Skip past tracks that fail to load (404, decode error) instead of
  // letting the music silently stall. Stops after one full failed cycle.
  useEffect(() => {
    const audio = musicAudioRef.current;
    const handleError = () => {
      failedTracksRef.current += 1;
      if (failedTracksRef.current >= dogTunesPaths.length) return;
      setCurrentTrackIndex((prev) => (prev + 1) % dogTunesPaths.length);
    };
    const handleCanPlay = () => {
      failedTracksRef.current = 0;
    };
    audio.addEventListener("error", handleError);
    audio.addEventListener("canplay", handleCanPlay);
    return () => {
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, [dogTunesPaths.length]);

  useEffect(() => {
    const newVolume = volumeLevel === "low" ? 0.3 : 0.7;
    musicAudioRef.current.volume = newVolume;
  }, [volumeLevel]);

  useEffect(() => {
    return () => {
      musicAudioRef.current.pause();
      musicAudioRef.current.src = "";
    };
  }, []);

  useEffect(() => {
    audioRef.current.volume = 0.5;
    backAudioRef.current.volume = 0.5;
    trickAudioRef.current.volume = 0.5;
    downloadAudioRef.current.volume = 0.5;
    audioRef.current.load();
    backAudioRef.current.load();
    trickAudioRef.current.load();
    downloadAudioRef.current.load();
    console.log("Audio files initialized");

    audioRef.current.addEventListener("play", () => console.log("Forward bark playing"));
    backAudioRef.current.addEventListener("play", () => console.log("Back bark playing"));
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      console.log("Key pressed:", e.key);
      if (e.key === "Escape" && showLargeImage) {
        console.log("Escape pressed, closing image");
        setShowLargeImage(false);
        setResult(null);
        setAttemptsLeft(3);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showLargeImage]);

 // Update this section in the setupControls function
// This goes inside the useEffect where we set up the drag functionality

// Update this section in the setupControls function
// This goes inside the useEffect where we set up the drag functionality

useEffect(() => {
  const setupControls = () => {
    // First, make sure the container exists
    const container = document.getElementById("dog-game-container");
    if (!container) {
      console.error("Container element not found");
      return;
    }

    console.log("Setting up drag controls for mini-game");
    
    // Remove smooth scrolling which can interfere with direct drag
    container.style.scrollBehavior = "auto";
    
    // Set initial state variables
    let isDraggingInternal = false;
    let startX, startY, scrollLeft, scrollTop;
    let hasMoved = false; // Track if actually moved (for distinguishing clicks from drags)
    let startTime = 0; // Track when the mouse/touch started
    
    // --- DESKTOP MOUSE CONTROLS ---
    const handleMouseDown = (e) => {
      // Only handle left mouse button (IE uses 1 for left button, modern browsers use 0)
      if (e.button !== 0 && e.button !== 1) return;

      // Prevent default browser behavior that might interfere
      e.preventDefault();

      // Reset move tracking
      hasMoved = false;
      startTime = Date.now();

      // Record start position and current scroll position (IE-compatible)
      startX = e.pageX || (e.clientX + window.pageXOffset);
      startY = e.pageY || (e.clientY + window.pageYOffset);
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
      
      // Mark as potential drag starting
      isDraggingInternal = true;
      
      // Update cursor
      container.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
    };
    
    const handleMouseMove = (e) => {
      if (!isDraggingInternal) return;

      // Calculate how far the mouse has moved (IE-compatible)
      const x = e.pageX || (e.clientX + window.pageXOffset);
      const y = e.pageY || (e.clientY + window.pageYOffset);
      const moveDistance = Math.sqrt(
        Math.pow(startX - x, 2) + Math.pow(startY - y, 2)
      );
      
      // If moved more than a minimum threshold, consider it a drag
      if (moveDistance > 5) {
        hasMoved = true;
        setIsDragging(true);
        
        // Calculate how far to scroll based on mouse movement
        const walkX = (startX - x) * 1.5; // Increased multiplier
        const walkY = (startY - y) * 1.5;
        
        // Update scroll position directly
        container.scrollLeft = scrollLeft + walkX;
        container.scrollTop = scrollTop + walkY;
      }
    };
    
    const handleMouseUp = (e) => {
      // Process as a click if it wasn't a significant drag
      const endTime = Date.now();
      const clickDuration = endTime - startTime;
      
      if (isDraggingInternal && !hasMoved && clickDuration < 300 && gameStarted) {
        // This was a click, not a drag - handle hotspot detection
        const imgElement = document.querySelector("#game-image-container img");
        if (imgElement) {
          const rect = imgElement.getBoundingClientRect();
          // The hotspot is stored in container coordinates (0-5120 mobile,
          // 0-10240 desktop) and the image is rendered at exactly those
          // container dimensions via width:100%/height:100%, so the raw
          // click offset within the img IS the container coordinate.
          // Do NOT scale by naturalWidth/naturalHeight — that introduced a
          // miss whenever the source PNG's intrinsic resolution differed
          // from the rendered container size.
          const scaledX = e.clientX - rect.left;
          const scaledY = e.clientY - rect.top;

          console.log(`Click at X: ${scaledX}, Y: ${scaledY}`);
          console.log(`Hotspot: X1=${hotspot.x1}, X2=${hotspot.x2}, Y1=${hotspot.y1}, Y2=${hotspot.y2}`);
          
          // Check if click is in hotspot
          if (
            scaledX >= hotspot.x1 &&
            scaledX <= hotspot.x2 &&
            scaledY >= hotspot.y1 &&
            scaledY <= hotspot.y2
          ) {
            console.log("You clicked inside the hotspot!");
            setResult("Winner!");
          } else {
            console.log("You clicked outside the hotspot.");
            setWrongGuess(true);
            
            // Reset wrong guess state after animation
            setTimeout(() => {
              setWrongGuess(false);
            }, 800);
            
            setAttemptsLeft((prev) => {
              const newAttempts = prev - 1;
              if (newAttempts <= 0) {
                setResult("Game Over");
              }
              return newAttempts;
            });
          }
        }
      }
      
      // End dragging state
      isDraggingInternal = false;
      hasMoved = false;
      
      // Small delay before resetting isDragging state
      setTimeout(() => {
        setIsDragging(false);
      }, 50);
      
      // Reset cursor
      container.style.cursor = "grab";
      document.body.style.userSelect = "";
    };
    
    // --- MOBILE TOUCH CONTROLS ---
    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      
      // Reset move tracking
      hasMoved = false;
      startTime = Date.now();
      
      // Record initial touch position and current scroll position
      isDraggingInternal = true;
      
      startX = e.touches[0].pageX;
      startY = e.touches[0].pageY;
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
    };
    
    const handleTouchMove = (e) => {
      if (!isDraggingInternal || e.touches.length !== 1) return;
      
      // Calculate how far the touch has moved
      const x = e.touches[0].pageX;
      const y = e.touches[0].pageY;
      const moveDistance = Math.sqrt(
        Math.pow(startX - x, 2) + Math.pow(startY - y, 2)
      );
      
      // If moved more than a minimum threshold, consider it a drag
      if (moveDistance > 10) { // Higher threshold for touch
        hasMoved = true;
        setIsDragging(true);
        
        // Higher multiplier for touch to make it more responsive
        const walkX = (startX - x) * 2.5;
        const walkY = (startY - y) * 2.5;
        
        // Directly update scroll position
        container.scrollLeft = scrollLeft + walkX;
        container.scrollTop = scrollTop + walkY;
      }
      
      // Only prevent default if we're actually dragging
      if (hasMoved) {
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e) => {
      // Process as a tap if it wasn't a significant drag
      const endTime = Date.now();
      const tapDuration = endTime - startTime;
      
      if (isDraggingInternal && !hasMoved && tapDuration < 300 && gameStarted) {
        // This was a tap, not a drag - handle hotspot detection
        const imgElement = document.querySelector("#game-image-container img");
        if (imgElement && e.changedTouches && e.changedTouches.length > 0) {
          const rect = imgElement.getBoundingClientRect();
          // Same fix as the mouse handler: hotspot is in container coordinates,
          // which equal the rendered img coordinates (width:100%/height:100%).
          // No naturalWidth/naturalHeight scaling.
          const scaledX = e.changedTouches[0].clientX - rect.left;
          const scaledY = e.changedTouches[0].clientY - rect.top;

          console.log(`Tap at X: ${scaledX}, Y: ${scaledY}`);
          console.log(`Hotspot: X1=${hotspot.x1}, X2=${hotspot.x2}, Y1=${hotspot.y1}, Y2=${hotspot.y2}`);
          
          // Check if tap is in hotspot
          if (
            scaledX >= hotspot.x1 &&
            scaledX <= hotspot.x2 &&
            scaledY >= hotspot.y1 &&
            scaledY <= hotspot.y2
          ) {
            console.log("You tapped inside the hotspot!");
            setResult("Winner!");
          } else {
            console.log("You tapped outside the hotspot.");
            setWrongGuess(true);
            
            // Reset wrong guess state after animation
            setTimeout(() => {
              setWrongGuess(false);
            }, 800);
            
            setAttemptsLeft((prev) => {
              const newAttempts = prev - 1;
              if (newAttempts <= 0) {
                setResult("Game Over");
              }
              return newAttempts;
            });
          }
        }
      }
      
      // End dragging state
      isDraggingInternal = false;
      hasMoved = false;
      
      // Small delay before resetting isDragging state
      setTimeout(() => {
        setIsDragging(false);
      }, 50);
    };

    // Backup click handler for IE/Edge compatibility
    const handleClick = (e) => {
      // Only process if game is active and we haven't already processed this click
      if (!gameStarted || isDraggingInternal || hasMoved) return;

      const imgElement = document.querySelector("#game-image-container img");
      if (imgElement && e.target.tagName === 'IMG') {
        const rect = imgElement.getBoundingClientRect();
        // Hotspot is in container coordinates; image fills the container at
        // 100%/100% so no naturalWidth/naturalHeight scaling is needed.
        const scaledX = e.clientX - rect.left;
        const scaledY = e.clientY - rect.top;

        console.log(`Backup click handler - Click at X: ${scaledX}, Y: ${scaledY}`);
        console.log(`Hotspot: X1=${hotspot.x1}, X2=${hotspot.x2}, Y1=${hotspot.y1}, Y2=${hotspot.y2}`);

        // Check if click is in hotspot
        if (
          scaledX >= hotspot.x1 &&
          scaledX <= hotspot.x2 &&
          scaledY >= hotspot.y1 &&
          scaledY <= hotspot.y2
        ) {
          console.log("You clicked inside the hotspot! (backup handler)");
          setResult("Winner!");
        } else {
          console.log("You clicked outside the hotspot. (backup handler)");
          setWrongGuess(true);

          setTimeout(() => {
            setWrongGuess(false);
          }, 800);

          setAttemptsLeft((prev) => {
            const newAttempts = prev - 1;
            if (newAttempts <= 0) {
              setResult("Game Over");
            }
            return newAttempts;
          });
        }
      }
    };

    // Add all event listeners with the right options
    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('click', handleClick); // Backup for IE/Edge

    // Touch events need { passive: false } to allow preventDefault()
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    // Cleanup all event listeners when the component unmounts
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('click', handleClick);

      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  };
  
  // Set up controls when showLargeImage becomes true
  if (showLargeImage) {
    // Use a slightly longer delay to make sure the DOM is fully ready
    setTimeout(setupControls, 800);
  }
  
  // Make sure to clean up when unmounting
  return () => {
    const container = document.getElementById("dog-game-container");
    if (container) {
      container.style.scrollBehavior = "smooth"; // Reset to default
    }
  };
}, [showLargeImage, setIsDragging, hotspot, gameStarted, setResult, setWrongGuess, setAttemptsLeft]);

  // Timer countdown effect
  useEffect(() => {
    if (gameStarted && !result && timeLeft > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            setResult("Game Over");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }

    // Cleanup timer when game ends
    if (result && timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  }, [gameStarted, result, timeLeft]);

// Also, remove or comment out the onClick event on the image element in the container-fix implementation
// The above useEffect will handle the clicks now


  
  const playBarkSound = () => {
    console.log("Attempting to play sound...");
    try {
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .then(() => console.log("Sound played successfully"))
        .catch((error) => console.error("Error playing sound:", error));
    } catch (error) {
      console.error("Error in playBarkSound:", error);
    }
  };

  const playBackBarkSound = () => {
    console.log("Attempting to play back sound...");
    try {
      backAudioRef.current.currentTime = 0;
      backAudioRef.current
        .play()
        .then(() => console.log("Back sound played successfully"))
        .catch((error) => console.error("Error playing back sound:", error));
    } catch (error) {
      console.error("Error in playBackBarkSound:", error);
    }
  };

  const playTrickBarkSound = () => {
    console.log("Attempting to play trick sound...");
    try {
      trickAudioRef.current.currentTime = 0;
      trickAudioRef.current
        .play()
        .then(() => console.log("Trick sound played successfully"))
        .catch((error) => console.error("Error playing trick sound:", error));
    } catch (error) {
      console.error("Error in playTrickBarkSound:", error);
    }
  };

  const playDownloadBarkSound = () => {
    console.log("Attempting to play download sound...");
    try {
      downloadAudioRef.current.currentTime = 0;
      downloadAudioRef.current
        .play()
        .then(() => console.log("Download sound played successfully"))
        .catch((error) => console.error("Error playing download sound:", error));
    } catch (error) {
      console.error("Error in playDownloadBarkSound:", error);
    }
  };

  const handleMusicPlayPause = (e) => {
    e.stopPropagation();
    if (!isMusicPlaying) {
      musicAudioRef.current
        .play()
        .then(() => {
          setIsMusicPlaying(true);
          setMusicPlayedBeforeSpeech(true);
        })
        .catch((err) => console.log("Music play error:", err));
    } else {
      musicAudioRef.current.pause();
      setIsMusicPlaying(false);
      setMusicPlayedBeforeSpeech(false);
    }
  };

  const handleSkipTrack = (e) => {
    e.stopPropagation();
    const nextIndex = (currentTrackIndex + 1) % dogTunesPaths.length;
    setCurrentTrackIndex(nextIndex);
  };

  const handleVolumeToggle = (e) => {
    e.stopPropagation();
    setVolumeLevel((prev) => (prev === "low" ? "high" : "low"));
  };

  // Handler for auth popup close
  const handleAuthClose = () => {
    setShowAuthPopup(false);
  };

  return (
    <div>
      <SectionTitle>Retro Dog Cards</SectionTitle>
      <MotionDivider />

      {/* Dog Card Container with Navigation Buttons */}
      <div
        style={{
          // Minimal padding & smaller radius on mobile
          padding: window.innerWidth < 600 ? "0.25rem" : "2rem",
          borderRadius: window.innerWidth < 600 ? "0.25rem" : "16px",
          backgroundColor: "#1a237e",
          color: "#fff",
          width: "100%",
          // Card/game-board shape on desktop: cap at the same ~tablet width
          // as the header (site name + mansion icon) instead of running edge
          // to edge across wide monitors.
          maxWidth: window.innerWidth < 600 ? "100%" : "1024px",
          boxSizing: "border-box",
          margin: "1rem auto", // centers horizontally, top/bottom margin
        }}
      >
        {/* Outer flex to center our sub-container */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          {/* Sub-container that keeps arrows near the dog card on desktop */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              // On desktop, a larger gap so arrows are ~1 inch from the card
              gap: window.innerWidth < 600 ? "0.5rem" : "2rem",
              maxWidth: "40rem", // ~640px wide so they don't stretch too far
              width: "100%",
              margin: "0 auto",
              boxSizing: "border-box",
            }}
          >
            {/* Left Navigation Button */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigateBack();
                playBackBarkSound();
              }}
              sx={{
                background: "linear-gradient(145deg, #3f51b5, #283593)",
                color: "white",
                width: "50px",
                height: "50px",
                flexShrink: 0,
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>

            {/* Dog Card */}
            <div
              ref={cardRef}
              style={{
                backgroundColor: "#1a237e",
                padding: window.innerWidth < 600 ? "0.5rem" : "1rem",
                borderRadius: window.innerWidth < 600 ? "0.25rem" : "8px",
                cursor: "pointer",
                width: "100%",
                boxSizing: "border-box",
                // Let the card shrink below the breed name's word width so the
                // nav arrows always stay on-screen (flex min-width:auto would
                // otherwise push the right arrow off narrow viewports).
                minWidth: 0,
              }}
            >
              {loading ? (
                <div>
                  {/* Loading Breed Name - maintains same space as real breed name */}
                  <h2
                    style={{
                      fontFamily: "'Press Start 2P', cursive",
                      marginBottom: "1rem",
                      fontSize: "clamp(1rem, 4vw, 1.75rem)",
                      textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                      textAlign: "center",
                      width: "100%",
                      padding: window.innerWidth < 600 ? "0.25rem" : "0 10px",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      hyphens: "auto",
                      boxSizing: "border-box",
                      color: "#FFFFFF",
                      opacity: 0.8,
                    }}
                  >
                    {isInitialLoad ? "Loading Breed..." : "Loading Next Breed..."}
                  </h2>

                  {/* Loading Image Container - maintains same dimensions */}
                  <div style={{ margin: "1rem 0", width: "100%" }}>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: window.innerWidth < 600 ? "100%" : "256px",
                        height: window.innerWidth < 600 ? "200px" : "256px", // Fixed height to match typical image
                        margin: "0 auto",
                        boxSizing: "border-box",
                        backgroundColor: "#1a237e",
                        padding: window.innerWidth < 600 ? "2px" : "10px",
                        border: "2px dashed rgba(255,255,255,0.3)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: window.innerWidth < 600 ? "2rem" : "3rem",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      <div className="retro-loading-dots">
                        <div className="retro-dot"></div>
                        <div className="retro-dot"></div>
                        <div className="retro-dot"></div>
                      </div>
                    </div>
                  </div>

                  {/* Loading Description Section - maintains same space */}
                  <div
                    style={{
                      margin: window.innerWidth < 600 ? "0.5rem 0" : "1.5rem auto",
                      width: "100%",
                      maxWidth: window.innerWidth < 600 ? "100%" : "600px",
                      padding: window.innerWidth < 600 ? "0.5rem" : "1rem",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      borderRadius: "4px",
                      minHeight: window.innerWidth < 600 ? "80px" : "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        padding: window.innerWidth < 600 ? "0.25rem" : "0.5rem",
                        fontSize: window.innerWidth < 600 ? "0.85rem" : "1rem",
                        textAlign: "center",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        hyphens: "auto",
                        width: "100%",
                        lineHeight: window.innerWidth < 600 ? "1.3" : "1.5",
                        color: "rgba(255,255,255,0.7)",
                        fontStyle: "italic",
                      }}
                    >
                      Generating breed description...
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Breed Name */}
                  <h2
                    style={{
                      fontFamily: "'Press Start 2P', cursive",
                      marginBottom: "1rem",
                      fontSize: "clamp(1rem, 4vw, 1.75rem)",
                      textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                      textAlign: "center",
                      width: "100%",
                      padding: window.innerWidth < 600 ? "0.25rem" : "0 10px",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      hyphens: "auto",
                      boxSizing: "border-box",
                    }}
                  >
                    {loadingAction ? "Loading Next Command..." : currentBreed}
                  </h2>

                  {/* Image Container */}
                  <div style={{ margin: "1rem 0", width: "100%" }}>
                    {loadingAction ? (
                      <div
                        style={{
                          width: "100%",
                          maxWidth: window.innerWidth < 600 ? "100%" : "256px",
                          height: window.innerWidth < 600 ? "200px" : "256px",
                          margin: "0 auto",
                          boxSizing: "border-box",
                          backgroundColor: "#1a237e",
                          padding: window.innerWidth < 600 ? "2px" : "10px",
                          border: "2px dashed rgba(255,255,255,0.3)",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div className="retro-loading-dots">
                          <div className="retro-dot"></div>
                          <div className="retro-dot"></div>
                          <div className="retro-dot"></div>
                        </div>
                      </div>
                    ) : (
                      <img
                        src={currentImageUrl}
                        alt={`${currentBreed} sprite`}
                        title="Double-tap to play with this dog in 3D!"
                        onDoubleClick={openIn3DVoxels}
                        onTouchEnd={handleSpriteTap}
                        onError={(e) => {
                          console.error("Error loading image:", e.target.src);
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                        style={{
                          display: "block",
                          width: "100%", // fill container
                          maxWidth: window.innerWidth < 600 ? "100%" : "256px",
                          height: "auto",
                          margin: "0 auto",
                          boxSizing: "border-box",
                          backgroundColor: "#1a237e",
                          padding: window.innerWidth < 600 ? "2px" : "10px",
                          cursor: "pointer",
                        }}
                      />
                    )}
                  </div>

                  {/* Description Section */}
                  <div
                    style={{
                      margin: window.innerWidth < 600 ? "0.5rem 0" : "1.5rem auto",
                      width: "100%",
                      maxWidth: window.innerWidth < 600 ? "100%" : "600px",
                      padding: window.innerWidth < 600 ? "0.5rem" : "1rem",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      borderRadius: "4px",
                      minHeight: window.innerWidth < 600 ? "80px" : "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    <p
                      title="Double-tap to play with this dog in 3D!"
                      onDoubleClick={openIn3DVoxels}
                      onTouchEnd={handleSpriteTap}
                      style={{
                        margin: 0,
                        padding: window.innerWidth < 600 ? "0.25rem" : "0.5rem",
                        fontSize: window.innerWidth < 600 ? "0.85rem" : "1rem",
                        textAlign: "center",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        hyphens: "auto",
                        width: "100%",
                        lineHeight: window.innerWidth < 600 ? "1.3" : "1.5",
                      }}
                    >
                      {currentDescription}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Navigation Button */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                showNextBreed();
              }}
              sx={{
                background: "linear-gradient(145deg, #3f51b5, #283593)",
                color: "white",
                width: "50px",
                height: "50px",
                flexShrink: 0,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: window.innerWidth < 600 ? "0.5rem" : "1rem",
            marginTop: "1rem",
            flexWrap: window.innerWidth < 400 ? "wrap" : "nowrap",
          }}
        >
          <Tooltip title="Search" arrow componentsProps={dogButtonTooltipProps}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setShowLargeImageWithSprite();
            }}
            sx={{
              background: "linear-gradient(145deg, #3f51b5, #283593)",
              color: "white",
              width: window.innerWidth < 600 ? "50px" : "60px",
              height: window.innerWidth < 600 ? "50px" : "60px",
              borderRadius: "50%",
              boxShadow:
                "4px 4px 10px rgba(0, 0, 0, 0.2), -4px -4px 10px rgba(255, 255, 255, 0.1)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            <SearchIcon fontSize={window.innerWidth < 600 ? "medium" : "large"} />
          </IconButton>
          </Tooltip>

          {/* Download Button */}
          <Tooltip title="Download" arrow componentsProps={dogButtonTooltipProps}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              downloadCard();
              playDownloadBarkSound();
            }}
            sx={{
              background: "linear-gradient(145deg, #3f51b5, #283593)",
              color: "white",
              width: window.innerWidth < 600 ? "50px" : "60px",
              height: window.innerWidth < 600 ? "50px" : "60px",
              borderRadius: "50%",
              boxShadow:
                "4px 4px 10px rgba(0, 0, 0, 0.2), -4px -4px 10px rgba(255, 255, 255, 0.1)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            <DownloadIcon fontSize={window.innerWidth < 600 ? "medium" : "large"} />
          </IconButton>
          </Tooltip>

          {/* Play Description Button */}
          <Tooltip title="Learn" arrow componentsProps={dogButtonTooltipProps}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              speakDescription();
            }}
            sx={{
              background: "linear-gradient(145deg, #3f51b5, #283593)",
              color: "white",
              width: window.innerWidth < 600 ? "50px" : "60px",
              height: window.innerWidth < 600 ? "50px" : "60px",
              borderRadius: "50%",
              boxShadow:
                "4px 4px 10px rgba(0, 0, 0, 0.2), -4px -4px 10px rgba(255, 255, 255, 0.1)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              outline: "none",
            }}
          >
            <HearingIcon fontSize={window.innerWidth < 600 ? "medium" : "large"} />
          </IconButton>
          </Tooltip>

          {/* Generate Random Action Button (span wrapper so the tooltip still
              anchors while the button is disabled) */}
          <Tooltip title="Command" arrow componentsProps={dogButtonTooltipProps}>
          <span style={{ display: "inline-flex" }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (!loadingAction) {
                generateRandomAction();
              }
            }}
            disabled={loadingAction}
            sx={{
              background: loadingAction
                ? "linear-gradient(145deg, #666, #444)"
                : "linear-gradient(145deg, #3f51b5, #283593)",
              color: "white",
              width: window.innerWidth < 600 ? "50px" : "60px",
              height: window.innerWidth < 600 ? "50px" : "60px",
              borderRadius: "50%",
              boxShadow: loadingAction
                ? "inset 2px 2px 5px rgba(0, 0, 0, 0.5)"
                : "4px 4px 10px rgba(0, 0, 0, 0.2), -4px -4px 10px rgba(255, 255, 255, 0.1)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              outline: "none",
              cursor: loadingAction ? "not-allowed" : "pointer",
              opacity: loadingAction ? 0.8 : 1,
              "&.Mui-disabled": {
                color: "white",
                opacity: 0.8,
              }
            }}
          >
            <RecordVoiceOverIcon fontSize={window.innerWidth < 600 ? "medium" : "large"} />
          </IconButton>
          </span>
          </Tooltip>
        </div>


        {/* Large Image Section - Improved Mobile Experience */}
        {showLargeImage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              overflow: "hidden",
              zIndex: 9999,
              WebkitOverflowScrolling: "touch",
            }}
            onClick={() => {
              if (imageLoaded && !gameStarted) {
                setGameStarted(true);
                setLoadingMessage("");
              }
            }}
          >
            {/* Instructions Header - Only show when NOT started AND image is loaded */}
            {!gameStarted && imageLoaded && (
              <div
                style={{
                  position: "fixed",
                  top: "10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#FFFFFF",
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: window.innerWidth < 600 ? "10px" : "14px",
                  textAlign: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  padding: window.innerWidth < 600 ? "6px 10px 25px 10px" : "10px 20px 25px 20px", // Increased bottom padding
                  borderRadius: "8px",
                  zIndex: 10001,
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
                  width: window.innerWidth < 600 ? "95%" : "90%",
                  maxHeight: window.innerWidth < 600 ? "40vh" : "auto",
                  overflowY: window.innerWidth < 600 ? "auto" : "visible",
                }}
              >
                {/* Music Controls */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: window.innerWidth < 600 ? "0.5rem" : "1rem",
                    marginBottom: "8px",
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMusicPlayPause();
                    }}
                    style={{
                      border: "2px solid #fff",
                      borderRadius: "4px",
                      backgroundColor: "#1a237e",
                      color: "#fff",
                      width: window.innerWidth < 600 ? "40px" : "50px",
                      height: window.innerWidth < 600 ? "40px" : "50px",
                      fontFamily: "'Press Start 2P', cursive",
                      cursor: "pointer",
                      fontSize: window.innerWidth < 600 ? "12px" : "16px",
                    }}
                  >
                    {isMusicPlaying ? "⏸" : "▶"}
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSkipTrack(e);
                    }}
                    style={{
                      border: "2px solid #fff",
                      borderRadius: "4px",
                      backgroundColor: "#1a237e",
                      color: "#fff",
                      width: window.innerWidth < 600 ? "40px" : "50px",
                      height: window.innerWidth < 600 ? "40px" : "50px",
                      fontFamily: "'Press Start 2P', cursive",
                      cursor: "pointer",
                      fontSize: window.innerWidth < 600 ? "12px" : "16px",
                    }}
                  >
                    ⏭
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVolumeToggle(e);
                    }}
                    style={{
                      border: "2px solid #fff",
                      borderRadius: "4px",
                      backgroundColor: "#1a237e",
                      color: "#fff",
                      width: window.innerWidth < 600 ? "40px" : "50px",
                      height: window.innerWidth < 600 ? "40px" : "50px",
                      fontFamily: "'Press Start 2P', cursive",
                      cursor: "pointer",
                      fontSize: window.innerWidth < 600 ? "12px" : "16px",
                    }}
                  >
                    {volumeLevel === "low" ? "🔈" : "🔊"}
                  </button>
                </div>

                {/* Show isolated sprite in the instructions */}
                {transparentSpriteUrl && (
                  <div style={{
                    margin: "10px auto",
                    width: "100px",
                    height: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}>
                    <img
                      src={transparentSpriteUrl}
                      alt={`${currentBreed} sprite`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain"
                      }}
                    />
                  </div>
                )}

                <p style={{ margin: window.innerWidth < 600 ? "6px 0" : "10px 0" }}>
                  Find the {currentBreed}!
                </p>


                {window.innerWidth < 600 ? (
                  <p style={{ margin: "6px 0" }}>
                    Tap, drag, and pinch to navigate the image. Find and tap on the {currentBreed}!
                  </p>
                ) : (
                  <>
                    {navigator.platform.toLowerCase().includes("mac") ? (
                      <>
                        <p>Step 1 - Use two fingers on trackpad to scroll</p>
                        <p>Step 2 - Navigate to locate the dog breed</p>
                        <p>Step 3 - Click on Dog Breed (3 chances to win!)</p>
                      </>
                    ) : (
                      <>
                        <p>Step 1 - Use mouse wheel or middle-click to scroll</p>
                        <p>Step 2 - Navigate to locate the dog breed</p>
                        <p>Step 3 - Click on Dog Breed (3 chances to win!)</p>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Minimal Game Status Bar - Only show when game IS started */}
            {gameStarted && (
              <div style={{
                position: "fixed",
                top: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                padding: "4px 10px",
                background: "rgba(0, 0, 0, 0.8)",
                borderRadius: "4px",
                zIndex: 10001,
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
                className={wrongGuess ? "wrong-guess" : ""}
              >
                {/* Keep music controls in the gameplay mode too */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMusicPlayPause();
                  }}
                  style={{
                    border: "2px solid #fff",
                    borderRadius: "4px",
                    backgroundColor: "#1a237e",
                    color: "#fff",
                    width: "30px",
                    height: "30px",
                    fontFamily: "'Press Start 2P', cursive",
                    cursor: "pointer",
                    fontSize: "10px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  {isMusicPlaying ? "⏸" : "▶"}
                </button>

                <p style={{
                  margin: "0",
                  fontSize: window.innerWidth < 600 ? "8px" : "12px",
                  color: "#FFFFFF",
                  fontFamily: "'Press Start 2P', cursive"
                }}>
                  {wrongGuess ? "Nope! • " : ""}
                  <span className={wrongGuess ? "text-flash" : ""}>
                    Attempts: {attemptsLeft}
                  </span>
                  {" • "}
                  <span style={{ color: timeLeft <= 10 ? "#FF0000" : "#00FF00" }}>
                    Time: {timeLeft}s
                  </span>
                </p>
              </div>
            )}

            {/* "Ready!" message - Only show when game is not started AND image is loaded */}
            {!gameStarted && imageLoaded && (
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                  padding: window.innerWidth < 600 ? "20px" : "30px",
                  borderRadius: "8px",
                  border: "3px solid #00FF00",
                  color: "#00FF00",
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: window.innerWidth < 600 ? "18px" : "24px",
                  textAlign: "center",
                  zIndex: 10002,
                  maxWidth: "90%",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ marginBottom: "20px" }}>READY!</div>
                <p
                  style={{
                    color: "#FFFFFF",
                    margin: "0",
                    fontSize: window.innerWidth < 600 ? "10px" : "14px",
                    padding: "0 10px",
                    lineHeight: "1.5",
                  }}
                >
                  {window.innerWidth < 600 ? "Tap to Start" : "Click Here to Start"}
                </p>
              </div>
            )}

            {/* Loading message - show when image is not loaded yet */}
            {!gameStarted && !imageLoaded && loadingMessage && (
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.9)",
                  padding: window.innerWidth < 600 ? "20px" : "30px",
                  borderRadius: "8px",
                  color: "#FFD700",
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: window.innerWidth < 600 ? "14px" : "18px",
                  textAlign: "center",
                  zIndex: 10002,
                  maxWidth: "90%",
                  boxSizing: "border-box",
                }}
              >
                {loadingMessage}
              </div>
            )}

            

            
            

            {/* Game Container */}
            <div
  id="dog-game-container"
  style={{
    height: "calc(100vh - 100px)",
    width: "100%",
    overflowY: "scroll",
    overflowX: "scroll",
    marginTop: gameStarted ? "60px" : (window.innerWidth < 600 ? "100px" : "380px"),
    scrollBehavior: "auto", // Changed from "smooth" to "auto" for better drag response
    WebkitOverflowScrolling: "touch",
    position: "relative",
    willChange: "scroll-position", // Performance optimization for scroll
    msOverflowStyle: "none", // Hide scrollbars on IE/Edge
    scrollbarWidth: "none", // Hide scrollbars on Firefox
  }}
  onScroll={(e) => {
    if (window.innerWidth < 600) {
      const container = e.currentTarget;
      const viewport = document.getElementById("minimap-viewport");
      if (viewport && container) {
        const percentX = container.scrollLeft / (10240 - window.innerWidth);
        const percentY = (container.scrollTop - 350) / (5760 - window.innerHeight);

        viewport.style.left = `${percentX * 80}px`;
        viewport.style.top = `${percentY * 45}px`;
      }
    }
  }}
>
  <div
    id="game-image-container"
    style={{
      transformOrigin: "0 0",
      position: "relative", // Ensure positioning context for absolute sprite
      width: window.innerWidth < 600 ? "5120px" : "10240px",  // Explicitly set dimensions
      height: window.innerWidth < 600 ? "2880px" : "5760px",
    }}
  >
    <img
      src={`${MEDIA_BASE_URL}/BigDogScapeMOON.png`}
      alt="Find the Dog Breed"
      onLoad={() => {
        setImageLoaded(true);
        setLoadingMessage("Ready!");
      }}
      onError={() => setLoadingMessage("Failed to Load Image")}
      style={{
        width: "100%",
        height: "100%",
        cursor: result ? "default" : "pointer",
        display: "block",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
    />

    {/* Add the transparent sprite overlay - only when game has started */}
    {gameStarted && miniGameData && transparentSpriteUrl && (
      <img
        src={transparentSpriteUrl}
        alt={`${currentBreed} sprite`}
        style={{
          position: "absolute",
          left: `${miniGameData.position.x}px`,
          top: `${miniGameData.position.y}px`,
          width: `${miniGameData.position.width}px`,
          height: `${miniGameData.position.height}px`,
          pointerEvents: "none", // Allows click to pass through to background
        }}
      />
    )}
  </div>
</div>
          </div>
        )}

        {/* Result Section */}
        {result && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10001,
            }}
            onClick={() => {
              // Ignore the ghost click mobile browsers fire right after the
              // winning tap — otherwise the overlay closes itself instantly.
              if (Date.now() - resultShownAtRef.current < 600) return;
              setShowLargeImage(false);
              setResult(null);
              setAttemptsLeft(3);
            }}
          >
            <h1
              style={{
                fontFamily: "'Press Start 2P', cursive",
                color: result === "Winner!" ? "#00FF00" : "#FF0000",
                fontSize: "48px",
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              {result}
            </h1>
            <Typography
              style={{
                fontFamily: "'Press Start 2P', cursive",
                color: "#FFFFFF",
                fontSize: "16px",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              Hit Back or Hit (Esc) to exit / retry
            </Typography>
          </div>
        )}

<style>{`
  @keyframes spinAndPulse {
    0% {
      transform: rotate(0deg) scale(1);
      transform-origin: center;
    }
    50% {
      transform: rotate(180deg) scale(1.1);
      transform-origin: center;
    }
    100% {
      transform: rotate(360deg) scale(1);
      transform-origin: center;
    }
  }

  @keyframes retroDot1 {
    0%, 9% {
      opacity: 0;
    }
    10%, 39% {
      opacity: 1;
    }
    40%, 100% {
      opacity: 0;
    }
  }

  @keyframes retroDot2 {
    0%, 19% {
      opacity: 0;
    }
    20%, 49% {
      opacity: 1;
    }
    50%, 100% {
      opacity: 0;
    }
  }

  @keyframes retroDot3 {
    0%, 29% {
      opacity: 0;
    }
    30%, 59% {
      opacity: 1;
    }
    60%, 100% {
      opacity: 0;
    }
  }

  .retro-loading-dots {
    display: flex;
    gap: 4px;
    justify-content: center;
    align-items: center;
  }

  .retro-dot {
    width: 8px;
    height: 8px;
    background-color: #FFFFFF;
    border-radius: 0;
    opacity: 0;
    animation-duration: 1.8s;
    animation-iteration-count: infinite;
    animation-timing-function: steps(1, end);
  }

  .retro-dot:nth-child(1) {
    animation-name: retroDot1;
  }

  .retro-dot:nth-child(2) {
    animation-name: retroDot2;
  }

  .retro-dot:nth-child(3) {
    animation-name: retroDot3;
  }

  #dog-game-container button:focus,
  #dog-game-container button:active {
    outline: none;
    box-shadow: none;
    background: linear-gradient(145deg, #3f51b5, #283593);
  }

  #dog-game-container button {
    -webkit-tap-highlight-color: transparent;
  }
  
 @keyframes wrongGuessFlash {
  0% { background-color: rgba(0, 0, 0, 0.5); }
  50% { background-color: rgba(200, 0, 0, 0.9); } /* Brighter red */
  100% { background-color: rgba(0, 0, 0, 0.5); }
}

.wrong-guess {
  animation: wrongGuessFlash 0.8s ease;
}

@keyframes textFlash {
  0% { color: #FFFFFF; }
  50% { color: #FF3333; } /* Brighter red */
  100% { color: #FFFFFF; }
}

.text-flash {
  animation: textFlash 0.8s ease;
}

#dog-game-container {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
}

#dog-game-container:active {
  cursor: grabbing;
}

.is-dragging {
  cursor: grabbing !important;
}

/* Add smooth inertia scrolling for iOS Safari */
@media (pointer: coarse) {
  #dog-game-container {
    -webkit-overflow-scrolling: touch;
  }
}

/* Hide scrollbars but keep functionality */
#dog-game-container::-webkit-scrollbar {
  display: none;
}

/* Ensure drag cursor is consistently shown */
#dog-game-container.is-dragging,
#dog-game-container.is-dragging * {
  cursor: grabbing !important;
  user-select: none !important;
}

`}</style>

      </div>

      {/* Auth Popup */}
      {showAuthPopup && <AuthPopup onClose={handleAuthClose} fromDogCards={true} />}


    </div>
  );
}


export default DogSprite;

